import { useState, useEffect } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader, Card } from "@/components/bits";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, Loader2, FileSpreadsheet, History, RotateCcw, FileText, Check, SkipForward, ChevronLeft } from "lucide-react";

const FILTERS = ["ALL", "ORDER", "RECEIVED", "INSTALLED", "SALVAGE", "NEED REVIEW", "DUPLICATE"];
const REV = { AUTO_READY: "COMPLETE", APPROVED: "COMPLETE", REVIEW: "REVIEW", MANUAL_REVIEW: "PROBLEM", DUPLICATE: "PROBLEM" };

function Diff({ reported, parsed }) {
  const r = reported, p = parsed || 0;
  if (r == null) return <span className="text-gray-400">{p} parsed</span>;
  const d = r - p;
  return <span className={d === 0 ? "text-emerald-600" : "text-amber-600"}>{p}/{r}{d !== 0 ? ` (Δ${d})` : ""}</span>;
}

export default function AIImport() {
  const [tab, setTab] = useState("import");
  const [uploading, setUploading] = useState(false);
  const [batch, setBatch] = useState(null);
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("ALL");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = () => api.get("/migration/batches").then((r) => setHistory(r.data)).catch(() => {});
  useEffect(() => { if (tab === "history") loadHistory(); }, [tab]);

  const onFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/migration/analyze", fd);
      setBatch(data.batch); setRows(data.rows);
      toast.success(`${data.rows.length} baris terdeteksi. Review sebelum migrasi.`);
    } catch (err) { toast.error(apiError(err, "Analisa gagal")); }
    finally { setUploading(false); }
  };

  const rowAction = async (id, action, fields) => {
    try { const { data } = await api.patch(`/migration/rows/${id}`, { action, fields });
      setRows((rs) => rs.map((r) => (r.id === id ? data : r))); }
    catch (err) { toast.error(apiError(err)); }
  };

  const confirmMigration = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/migration/batches/${batch.id}/confirm`);
      toast.success(`Migrasi selesai: ${data.imported} record ditulis.`);
      setBatch(null); setRows([]); }
    catch (err) { toast.error(apiError(err, "Migrasi gagal")); }
    finally { setBusy(false); }
  };

  const rollback = async (bid) => {
    if (!window.confirm("Rollback batch ini? Semua data hasil migrasi batch ini akan dihapus.")) return;
    try { const { data } = await api.post(`/migration/batches/${bid}/rollback`);
      toast.success(`Rollback selesai. ${Object.values(data.deleted).reduce((a, b) => a + b, 0)} record dihapus.`);
      loadHistory(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const viewSource = async (bid) => {
    try { const r = await api.get(`/migration/batches/${bid}/source`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank"); }
    catch { toast.error("Gagal membuka sumber"); }
  };

  const fRows = rows.filter((r) => {
    if (filter === "ALL") return true;
    if (filter === "NEED REVIEW") return ["REVIEW", "MANUAL_REVIEW"].includes(r.review_status);
    if (filter === "DUPLICATE") return r.duplicate;
    return r.event_type === filter;
  });
  const summary = batch && {
    imp: rows.filter((r) => !r.skipped && !r.duplicate && ["AUTO_READY", "APPROVED"].includes(r.review_status)).length,
    review: rows.filter((r) => ["REVIEW", "MANUAL_REVIEW"].includes(r.review_status)).length,
    dup: rows.filter((r) => r.duplicate).length,
  };

  return (
    <div>
      <PageHeader title="AI Import — Migrasi Data Legacy" subtitle="Upload laporan lama (PDF/Excel/CSV/foto). AI ekstrak → review → CONFIRM. Tidak ada data ditulis sebelum konfirmasi.">
        <div className="flex gap-2">
          <Button variant={tab === "import" ? "default" : "outline"} size="sm" onClick={() => setTab("import")} data-testid="tab-import"><Upload size={14} className="mr-1" />Import</Button>
          <Button variant={tab === "history" ? "default" : "outline"} size="sm" onClick={() => setTab("history")} data-testid="tab-history"><History size={14} className="mr-1" />Riwayat</Button>
        </div>
      </PageHeader>

      {tab === "history" ? (
        <div className="grid gap-2" data-testid="migration-history">
          {history.length === 0 ? <Card className="p-8 text-center text-gray-500">Belum ada batch migrasi</Card> :
            history.map((b) => (
              <Card key={b.id} className="p-3 flex flex-wrap items-center justify-between gap-2" data-testid={`batch-${b.id}`}>
                <div className="min-w-0">
                  <div className="font-semibold text-gray-900 flex items-center gap-2"><FileText size={15} /> {b.filename}</div>
                  <div className="text-xs text-gray-500">{(b.period_start || "?")} → {(b.period_end || "?")} • {b.created_at?.slice(0, 16).replace("T", " ")} • {b.imported_by || b.created_by}</div>
                  <div className="text-xs text-gray-500 mt-0.5">Rows: R{b.parsed_counts?.RECEIVED || 0} / I{b.parsed_counts?.INSTALLED || 0} / S{b.parsed_counts?.SALVAGE || 0} • Imported {b.imported_rows ?? 0}</div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={b.status === "CONFIRMED" ? "COMPLETE" : b.status === "ROLLED_BACK" ? "PROBLEM" : "REVIEW"} />
                  <span className="text-xs text-gray-500">{b.status}</span>
                  <Button size="sm" variant="outline" onClick={() => viewSource(b.id)} data-testid={`view-source-${b.id}`}><FileText size={13} className="mr-1" />Source</Button>
                  {b.status === "CONFIRMED" && <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => rollback(b.id)} data-testid={`rollback-${b.id}`}><RotateCcw size={13} className="mr-1" />Rollback</Button>}
                </div>
              </Card>
            ))}
        </div>
      ) : !batch ? (
        <Card className="p-10 text-center">
          <FileSpreadsheet size={40} className="text-gray-300 mx-auto mb-3" />
          <div className="font-head font-semibold text-gray-800">Upload laporan legacy</div>
          <p className="text-sm text-gray-500 mt-1 mb-5 max-w-md mx-auto">Mendukung PDF, Excel, CSV, dan foto/scan. AI membaca section PART ORDER / MASUK / TERPASANG / SALVAGE, mewarisi context receiving & tanggal, lalu tampilkan preview.</p>
          <label>
            <input type="file" accept=".pdf,.xlsx,.xls,.csv,.jpg,.jpeg,.png,image/*" className="hidden" onChange={onFile} data-testid="migration-upload-input" />
            <Button asChild className="bg-gold text-white cursor-pointer"><span>{uploading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Upload size={16} className="mr-2" />} Upload & Analisa</span></Button>
          </label>
        </Card>
      ) : (
        <div>
          <Card className="p-4 mb-3">
            <div className="flex items-center justify-between gap-2 mb-3">
              <div>
                <div className="font-semibold text-gray-900">{batch.filename}</div>
                <div className="text-xs text-gray-500">Periode {batch.period_start || "?"} → {batch.period_end || "?"} • extract: {batch.extract_method} • Batch {batch.id.slice(0, 8)}</div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => { setBatch(null); setRows([]); }}><ChevronLeft size={16} />Batal</Button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
              <MigCard label="PART ORDER" body={<Diff reported={batch.reported?.order} parsed={batch.parsed_counts?.ORDER} />} />
              <MigCard label="PART RECEIVED" body={<Diff reported={batch.reported?.received} parsed={batch.parsed_counts?.RECEIVED} />} />
              <MigCard label="PART INSTALLED" body={<Diff reported={batch.reported?.installed} parsed={batch.parsed_counts?.INSTALLED} />} />
              <MigCard label="PART SALVAGE" body={<Diff reported={batch.reported?.salvage} parsed={batch.parsed_counts?.SALVAGE} />} />
              <MigCard label="RECONCILED" body={<span className="text-emerald-700">{batch.reconcile_matched} matched</span>} />
              <MigCard label="NEED REVIEW" body={<span className="text-amber-600">{summary.review}</span>} />
              <MigCard label="DUPLICATE" body={<span className="text-red-600">{summary.dup}</span>} />
              <MigCard label="READY IMPORT" body={<span className="text-gray-900 font-semibold">{summary.imp}</span>} />
            </div>
          </Card>

          <div className="flex gap-1.5 flex-wrap mb-2">
            {FILTERS.map((f) => <Button key={f} size="sm" variant={filter === f ? "default" : "outline"} onClick={() => setFilter(f)} data-testid={`filter-${f}`}>{f}</Button>)}
          </div>

          <Card className="overflow-hidden mb-4">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-gray-500 uppercase">
                  <tr>
                    <th className="text-left px-2 py-2">Tgl</th><th className="text-left px-2 py-2">Event</th>
                    <th className="text-left px-2 py-2">Nopol</th><th className="text-left px-2 py-2">Mobil</th>
                    <th className="text-left px-2 py-2">Asuransi</th><th className="text-left px-2 py-2">No Rcv</th>
                    <th className="text-left px-2 py-2">Ref</th><th className="text-left px-2 py-2">Part No</th>
                    <th className="text-left px-2 py-2">Nama</th><th className="text-center px-2 py-2">Qty</th>
                    <th className="text-left px-2 py-2">Supplier</th><th className="text-center px-2 py-2">Conf</th>
                    <th className="text-left px-2 py-2">Status</th><th className="text-left px-2 py-2">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {fRows.map((r) => (
                    <tr key={r.id} className={`border-t border-gray-100 ${r.skipped ? "opacity-40" : ""} ${r.duplicate ? "bg-red-50/40" : ""}`} data-testid={`mig-row-${r.id}`}>
                      <td className="px-2 py-1.5 whitespace-nowrap">{r.event_date || "—"}</td>
                      <td className="px-2 py-1.5">{r.event_type}</td>
                      <td className="px-2 py-1.5 font-mono-data font-semibold whitespace-nowrap">{r.nopol || "—"}{r.context_inherited ? <span title="context inherited" className="text-amber-500">*</span> : ""}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{[r.vehicle_brand, r.vehicle_model].filter(Boolean).join(" ") || "—"}</td>
                      <td className="px-2 py-1.5">{r.insurer || "—"}</td>
                      <td className="px-2 py-1.5 font-mono-data whitespace-nowrap">{r.receiving_number || "—"}</td>
                      <td className="px-2 py-1.5">{r.reference || "—"}</td>
                      <td className="px-2 py-1.5 font-mono-data">{r.part_number || "—"}</td>
                      <td className="px-2 py-1.5 max-w-[160px] truncate" title={r.part_name}>{r.part_name}</td>
                      <td className="text-center px-2 py-1.5">{r.qty}</td>
                      <td className="px-2 py-1.5">{r.supplier || "—"}</td>
                      <td className="text-center px-2 py-1.5">{Math.round((r.confidence || 0) * 100)}%</td>
                      <td className="px-2 py-1.5"><StatusBadge status={r.duplicate ? "PROBLEM" : REV[r.review_status] || "REVIEW"} /></td>
                      <td className="px-2 py-1.5">
                        <div className="flex gap-1">
                          {!r.skipped && r.review_status !== "APPROVED" && !r.duplicate && <button title="Approve" onClick={() => rowAction(r.id, "approve")} className="text-emerald-600" data-testid={`approve-${r.id}`}><Check size={14} /></button>}
                          {!r.skipped && <button title="Skip" onClick={() => rowAction(r.id, "skip")} className="text-gray-400" data-testid={`skip-${r.id}`}><SkipForward size={14} /></button>}
                          {r.skipped && <button title="Unskip" onClick={() => rowAction(r.id, "unskip")} className="text-blue-500">↺</button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <div className="sticky bottom-4 flex flex-col sm:flex-row gap-2 sm:justify-end items-stretch sm:items-center bg-white/80 backdrop-blur p-3 rounded-xl border border-gray-100">
            <span className="text-xs text-gray-500 sm:mr-auto">Akan diimport: <b>{summary.imp}</b> • Need review: <b className="text-amber-600">{summary.review}</b> • Duplicate skip: <b className="text-red-600">{summary.dup}</b></span>
            <Button onClick={confirmMigration} disabled={busy || summary.imp === 0} className="bg-gray-900" data-testid="confirm-migration-btn">
              {busy ? <Loader2 className="animate-spin mr-2" size={16} /> : <Check size={16} className="mr-2" />} CONFIRM MIGRATION ({summary.imp})
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function MigCard({ label, body }) {
  return (
    <div className="rounded-lg border border-gray-100 p-2.5">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">{label}</div>
      <div className="text-sm font-semibold mt-0.5">{body}</div>
    </div>
  );
}
